import { z } from "zod";
import { ValidatorInterface } from "../validator-interface";
import { getCheckboxGroupValues, hasRadioGroupValue } from "../utils/input-group-utils";

export class RequiredValidator extends ValidatorInterface {
    getZodSchema(baseSchema, options = {}) {
        const labelText = options.labelText || "This field";
        const fieldType = options.fieldType;
        const field = options.field;
        const message = field?.getAttribute("data-error-required") || `${labelText} is required`;

        // Special handling for select fields
        if (fieldType === "select") {
            const defaultValue = field?.getAttribute("data-default-value");
            return baseSchema.refine(val => val !== defaultValue && val.length > 0, { message });
        }
        if (fieldType === "checkbox") {
            return baseSchema.refine(
                () => {
                    // For checkboxes, we only care if it's checked, regardless of its value
                    return field.checked === true;
                },
                { message }
            );
        }
        if (fieldType === "checkboxGroup") {
            return baseSchema.refine(
                () => {
                    const selectedValues = getCheckboxGroupValues(field);
                    return selectedValues.length > 0;
                },
                { message }
            );
        }
        if (fieldType === "radioGroup") {
            return baseSchema.refine(() => hasRadioGroupValue(field), { message });
        }

        // Handle different base schema types
        if (baseSchema instanceof z.ZodString) {
            // Use min(1) for strings to check for non-empty
            return baseSchema.min(1, { message });
        } else if (baseSchema instanceof z.ZodNumber) {
            // For numbers, just ensure it's provided (non-NaN is handled by transform)
            // Zod's required() is implicit for non-optional schemas
            // We rely on the base type not being optional here.
            // If we need to explicitly forbid undefined/null for non-strings, refine is better.
            return baseSchema.refine(val => val !== undefined && val !== null, { message });
        } else if (baseSchema instanceof z.ZodBoolean) {
            // Ensure boolean is true if required (adjust logic as needed)
            return baseSchema.refine(val => val === true, { message });
        } else if (baseSchema instanceof z.ZodArray) {
            // Ensure array is not empty
            return baseSchema.nonempty({ message });
        }
        // Add more types as needed (e.g., ZodDate)

        // Default fallback: refine check for non-null/undefined
        // This might be too broad, consider specific type handling above
        return baseSchema.refine(val => val !== undefined && val !== null, { message });
    }

    getErrorMessage(field, options = {}) {
        const labelText = options.labelText || "This field";
        return options.message || `${labelText} is required`;
    }
}
